import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { tasksApi, timelineApi } from "../lib/api";
import { useCurrency } from "../context/CurrencyContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import TaskTimeline, { TimelinePill } from "../components/TaskTimeline";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { cn } from "../lib/utils";
import {
  ArrowLeft,
  Save,
  Trash2,
  Plus,
  FolderOpen,
  DollarSign,
  AlertCircle,
  CheckCircle2,
  Clock,
  Calendar,
  Send,
  Users,
  Play,
  ListTodo,
  User,
  ExternalLink,
  Palette,
  ChevronDown,
  ChevronUp,
  Building2,
  Split,
  FileSignature,
  Download,
  Pencil,
} from "lucide-react";
import { Switch } from "../components/ui/switch";
import ScopeChangeRequests from "../components/ScopeChangeRequests";
import SetupChatButton from "../components/SetupChatButton";
import ChangeHistoryBlock from "../components/ChangeHistoryBlock";
import BulkPriceRequestModal from "../components/BulkPriceRequestModal";
import RequestsGantt from "../components/RequestsGantt";
import InlineDatePicker from "../components/InlineDatePicker";
import TaskStageBadge from "../components/TaskStageBadge";

const CURRENCIES = ["EUR", "USD", "UAH"];

// Generate month options for next 12 months
const getMonthOptions = () => {
  const months = [];
  const now = new Date();
  for (let i = -2; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const label = d.toLocaleDateString("en-US", { year: "numeric", month: "short" });
    months.push({ value, label });
  }
  return months;
};

const PartnerProjectPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { rates, formatAmount: formatAmountRaw } = useCurrency();
  const monthOptions = useMemo(() => getMonthOptions(), []);
  
  // Format amount with proper currency support (default USD for TRP)
  const formatAmount = (amount, fromCurrency = "USD") => formatAmountRaw(amount, fromCurrency);
  
  const isAdmin = user?.role === "admin";
  const isPartner = user?.role === "partner";
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [project, setProject] = useState(null);
  const [services, setServices] = useState([]);
  const [tasks, setTasks] = useState([]);
  // Setup-chat: which task types map to a flow, so the row shows a chat icon.
  const [setupChatKeywords, setSetupChatKeywords] = useState([]);
  useEffect(() => {
    api.get("/setup-chat/service-types-with-flow")
      .then(({ data }) => setSetupChatKeywords(data?.keywords || []))
      .catch(() => setSetupChatKeywords([]));
  }, []);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [suggestedPrices, setSuggestedPrices] = useState({}); // {service_type: price}
  const [taskTimelines, setTaskTimelines] = useState({});
  const [priceRequests, setPriceRequests] = useState([]); // Pending/approved price requests
  const [requestsView, setRequestsView] = useState("list"); // list | gantt

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    client_name: "",
    notes: "",
    start_date: "",
    estimated_days: 0,
    deadline: "",
    communication_group: "",
  });
  
  // New service modal - keeping for admin only
  const [showNewService, setShowNewService] = useState(false);
  const [newService, setNewService] = useState({
    service_type: "",
    name: "",
    totalAmount: "",
    currency: "EUR",
    month: "",
    payments: [{ month: "", percent: 100, amount: "" }],
    splitPayments: false,
  });

  // Split payment dialog
  const [splitPaymentDialogOpen, setSplitPaymentDialogOpen] = useState(false);
  const [paymentToSplit, setPaymentToSplit] = useState(null);
  const [splitParts, setSplitParts] = useState([
    { percent: 50, month: "" },
    { percent: 50, month: "" },
  ]);
  const [splittingPayment, setSplittingPayment] = useState(false);

  // Edit payment date dialog
  const [editDateDialogOpen, setEditDateDialogOpen] = useState(false);
  const [paymentToEditDate, setPaymentToEditDate] = useState(null);
  const [newPlannedDate, setNewPlannedDate] = useState("");
  
  // New task modal
  const [showNewTask, setShowNewTask] = useState(false);
  const [newTask, setNewTask] = useState({
    name: "",
    task_type: "",
    description: "",
    brief: "",
    deadline: "",
    client_price: 0,
    quantity: 1, // For batch task creation
    sq_ft: 0, // For sq ft pricing
    is_vexos_task: false, // Toggle between VEXOS and regular task
    extra_360_points: 0, // Additional 360-tour points billed at $20/each
    // VEXOS components
    vexos: {
      base_fee_qty: 0, // Optional - checkbox toggle
      orbit_fee_qty: 0,
      unit_apartments_qty: 0,
      unit_villas_qty: 0,
      fee_360_qty: 0,
      orbit_animation: false, // $2500 first + $1000/extra
      custom_design: false,
      rush_deadline: false,
      client_invoice: 0, // For 5% commission calculation
    }
  });
  
  // Service base prices (for margin calculation)
  const [basePrices, setBasePrices] = useState({});
  
  // Service types with full info (including is_sq_ft_pricing)
  const [serviceTypesInfo, setServiceTypesInfo] = useState([]);
  
  // VEXOS fees
  const [vexosFees, setVexosFees] = useState({
    base_fee: 1500,
    orbit_fee: 400,
    unit_fee_apartments: 25,
    unit_fee_villas: 100,
    fee_360: 100,
    custom_design: 1000,
    rush_deadline: 500,
    vexos_commission_percent: 5
  });
  
  // Tasks section expanded
  const [tasksExpanded, setTasksExpanded] = useState(true);
  
  // Bulk price request modal
  const [showBulkRequestModal, setShowBulkRequestModal] = useState(false);

  useEffect(() => {
    if (projectId && projectId !== "new") {
      fetchData();
    } else {
      // New project mode
      setLoading(false);
    }
  }, [projectId]);

  // Update planned start/end on a price request — admin only (partner sees read-only chips).
  const handleUpdatePriceRequestDates = async (requestId, patch) => {
    try {
      const { data } = await api.patch(`/price-requests/${requestId}`, patch);
      setPriceRequests(prev => prev.map(r => r.id === requestId ? { ...r, ...data } : r));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update dates");
      throw error;
    }
  };

  const fetchData = async () => {
    try {
      // Fetch TRP project, tasks, service types, suggested prices, base prices, price requests, VEXOS fees, and sales_services
      const [projectRes, tasksRes, pricesRes, basePricesRes, priceRequestsRes, serviceTypesRes, vexosFeesRes, salesServicesRes] = await Promise.all([
        api.get(`/trp/projects/${projectId}`),
        api.get(`/tasks?project_id=${projectId}`).catch(() => ({ data: [] })),
        api.get(`/partner/suggested-prices`).catch(() => ({ data: {} })),
        api.get(`/settings/service-prices`).catch(() => ({ data: [] })),
        api.get(`/price-requests?project_id=${projectId}`).catch(() => ({ data: [] })),
        api.get(`/service-types/list`).catch(() => ({ data: [] })),
        api.get(`/settings/vexos-fees`).catch(() => ({ data: {} })),
        api.get(`/sales-services?project_id=${projectId}`).catch(() => ({ data: { items: [] } })),
      ]);
      
      const projectData = projectRes.data;
      setProject(projectData);
      setPriceRequests(priceRequestsRes.data || []);
      
      // Store full service type info from unified endpoint
      const serviceTypesData = serviceTypesRes.data || [];
      setServiceTypesInfo(serviceTypesData);
      // Set service types list (just names for dropdown)
      setServiceTypes(serviceTypesData.map(s => s.name));
      
      // Store VEXOS fees
      if (vexosFeesRes.data && Object.keys(vexosFeesRes.data).length > 0) {
        setVexosFees(vexosFeesRes.data);
      }
      
      // Build base prices map from service_prices OR from service_types partner_price
      const pricesMap = {};
      // First from service_prices (legacy)
      (basePricesRes.data || []).forEach(p => {
        pricesMap[p.service_type] = p.base_price;
      });
      // Then from service_types partner_price (new)
      serviceTypesData.forEach(s => {
        if (s.partner_price && s.partner_price > 0) {
          pricesMap[s.name] = s.partner_price;
        }
      });
      setBasePrices(pricesMap);
      
      // Use sales_services as the source of truth for payments (created by manager)
      const salesServicesData = salesServicesRes.data?.items || salesServicesRes.data || [];
      const salesPayments = salesServicesData.map(s => ({
        id: s.id,
        name: s.name || "Payment",
        amount: s.amount || 0,
        currency: s.currency || "EUR",
        is_paid: s.is_paid,
        paid_date: s.paid_date,
        planned_date: s.planned_date,
        month: s.month,
        payment_group_id: s.payment_group_id,
        type: "sales_service",
      }));
      setServices(salesPayments);
      
      setTasks(tasksRes.data || []);
      // serviceTypes already set above from serviceTypesRes
      setSuggestedPrices(pricesRes.data || {});
      
      // Set form data from project
      setFormData({
        name: projectData.name || "",
        client_name: projectData.client_name || "",
        notes: projectData.notes || "",
        start_date: projectData.start_date || projectData.deposit_date || "",
        estimated_days: projectData.estimated_days || 0,
        deadline: projectData.deadline || projectData.deposit_date || "",
        communication_group: projectData.communication_group || "",
      });
      
      // Fetch timeline progress for each task
      const tasksList = tasksRes.data || [];
      if (tasksList.length > 0) {
        const timelinesMap = {};
        await Promise.all(
          tasksList.map(async (task) => {
            try {
              const timelineRes = await timelineApi.getTaskTimeline(task.id);
              if (timelineRes.data) {
                timelinesMap[task.id] = timelineRes.data;
              }
            } catch (e) {
              // Task may not have timeline
            }
          })
        );
        setTaskTimelines(timelinesMap);
      }
    } catch (error) {
      console.error("Error fetching project:", error);
      toast.error("Failed to load project");
      navigate("/trp");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error("Project name is required");
      return;
    }
    
    setSaving(true);
    try {
      if (projectId === "new") {
        // Validate required fields for partner
        if (!formData.start_date || !formData.deadline) {
          toast.error("Start Date та Deadline обов'язкові поля");
          setSaving(false);
          return;
        }
        
        // Create new TRP project
        const res = await api.post("/trp/projects", {
          name: formData.name,
          project_type: "outsource",
          payment_plan: "50/50",
          total_amount: 0,
          notes: formData.notes,
          start_date: formData.start_date,
          deadline: formData.deadline,
          deposit_date: formData.deadline,
        });
        toast.success("Project created");
        navigate(`/trp/project/${res.data.id}`);
      } else {
        // Update existing TRP project
        await api.put(`/trp/projects/${projectId}`, {
          name: formData.name,
          notes: formData.notes,
          start_date: formData.start_date,
          deadline: formData.deadline,
          deposit_date: formData.deadline,
        });
        toast.success("Project saved");
        fetchData();
      }
    } catch (error) {
      toast.error("Failed to save project");
    } finally {
      setSaving(false);
    }
  };

  // Check if project is VEXOS type
  const isVexosProject = project?.project_type === "vexos";
  
  // Check if selected task type uses sq ft pricing
  const getSelectedTypeInfo = () => {
    if (!newTask.task_type) return null;
    return serviceTypesInfo.find(t => t.name === newTask.task_type);
  };
  
  const isSqFtPricing = () => {
    const typeInfo = getSelectedTypeInfo();
    return typeInfo?.is_sq_ft_pricing || false;
  };
  
  // Calculate VEXOS total
  const calculateVexosTotal = () => {
    const v = newTask.vexos;
    const fees = vexosFees;
    
    // Orbit Animation Rendering: $2500 first + $1000/extra
    // Based on orbit_fee_qty (number of orbits)
    let orbitAnimationCost = 0;
    if (v.orbit_animation && v.orbit_fee_qty > 0) {
      orbitAnimationCost = 2500 + Math.max(0, v.orbit_fee_qty - 1) * 1000;
    }
    
    const componentsTotal = 
      (fees.base_fee * (v.base_fee_qty || 0)) +
      (fees.orbit_fee * (v.orbit_fee_qty || 0)) +
      (fees.unit_fee_apartments * (v.unit_apartments_qty || 0)) +
      (fees.unit_fee_villas * (v.unit_villas_qty || 0)) +
      (fees.fee_360 * (v.fee_360_qty || 0)) +
      orbitAnimationCost +
      (v.custom_design ? fees.custom_design : 0) +
      (v.rush_deadline ? fees.rush_deadline : 0);
    
    const commission = (v.client_invoice || 0) * (fees.vexos_commission_percent / 100);
    
    return {
      componentsTotal,
      commission,
      orbitAnimationCost,
      total: componentsTotal + commission
    };
  };
  
  // Calculate sq ft based price
  const calculateSqFtPrice = () => {
    const typeInfo = getSelectedTypeInfo();
    // If type uses sq ft pricing, calculate; otherwise return 0
    if (!typeInfo?.is_sq_ft_pricing) return 0;
    const basePrice = basePrices[newTask.task_type] || 0;
    return basePrice * (newTask.sq_ft || 0);
  };

  const handleAddService = async () => {
    if (!newService.totalAmount) {
      toast.error("Enter amount");
      return;
    }
    
    const paymentName = newService.name || `Payment ${services.length + 1}`;
    const plannedDate = newService.payments[0]?.month ? `${newService.payments[0].month}-15` : null;
    
    try {
      // Add to payment_schedule of TRP project
      const currentSchedule = project?.payment_schedule || [];
      const newPayment = {
        id: `ps-${Date.now()}`,
        description: paymentName,
        amount: parseFloat(newService.totalAmount),
        planned_date: plannedDate,
        status: "pending",
      };
      
      await api.put(`/trp/projects/${projectId}`, {
        payment_schedule: [...currentSchedule, newPayment],
      });
      
      toast.success("Payment added");
      setShowNewService(false);
      fetchData();
    } catch (error) {
      toast.error("Failed to add payment");
    }
  };

  const handleAddTask = async () => {
    if (!newTask.name.trim()) {
      toast.error("Task name is required");
      return;
    }
    
    // For VEXOS tasks - create price request for approval (same as regular tasks)
    if (newTask.is_vexos_task) {
      const vexosTotal = calculateVexosTotal();
      if (vexosTotal.total <= 0) {
        toast.error("Please add at least one VEXOS component");
        return;
      }
      
      try {
        // Create price request for VEXOS (managers will create task after approval)
        await api.post("/price-requests", {
          project_id: projectId,
          task_name: newTask.name,
          task_type: "vexos",
          proposed_price: vexosTotal.total,
          quantity: 1,
          notes: newTask.description || "",
          brief: newTask.brief || "",
          deadline: newTask.deadline || "",
          base_price: 0,
          vexos_details: {
            ...newTask.vexos,
            components_total: vexosTotal.componentsTotal,
            commission: vexosTotal.commission,
          }
        });
        toast.success("VEXOS price request submitted. Waiting for PM approval.");
        setShowNewTask(false);
        setNewTask({
          name: "", task_type: "", description: "", brief: "", deadline: "",
          client_price: 0, quantity: 1, sq_ft: 0, is_vexos_task: false,
          vexos: { base_fee_qty: 0, orbit_fee_qty: 0, unit_apartments_qty: 0, unit_villas_qty: 0, fee_360_qty: 0, orbit_animation: false, custom_design: false, rush_deadline: false, client_invoice: 0 }
        });
        fetchData();
      } catch (error) {
        toast.error("Failed to submit VEXOS price request");
      }
      return;
    }
    
    // For regular tasks
    if (!newTask.task_type) {
      toast.error("Task type is required");
      return;
    }
    
    const basePrice = basePrices[newTask.task_type] || 0;
    let proposedPrice = parseFloat(newTask.client_price) || 0;
    const quantity = parseInt(newTask.quantity) || 1;
    
    // If sq ft pricing, calculate the price
    if (isSqFtPricing()) {
      proposedPrice = calculateSqFtPrice();
    }
    
    try {
      // Partner always creates price request for approval (never creates tasks directly)
      await api.post("/price-requests", {
        project_id: projectId,
        task_name: newTask.name,
        task_type: newTask.task_type,
        proposed_price: proposedPrice,
        quantity: quantity,
        notes: newTask.description || "",
        brief: newTask.brief || "",
        deadline: newTask.deadline || "",
        sq_ft: isSqFtPricing() ? newTask.sq_ft : null,
        base_price: basePrice,
        extra_360_points: parseInt(newTask.extra_360_points) || 0,
      });
      toast.success(`Price request submitted for ${quantity} item(s). Waiting for PM approval.`);

      setShowNewTask(false);
      setNewTask({
        name: "", task_type: "", description: "", brief: "", deadline: "",
        client_price: 0, quantity: 1, sq_ft: 0, is_vexos_task: false, extra_360_points: 0,
        vexos: { base_fee_qty: 1, orbit_fee_qty: 0, unit_apartments_qty: 0, unit_villas_qty: 0, fee_360_qty: 0, custom_design: false, rush_deadline: false, client_invoice: 0 }
      });
      fetchData();
    } catch (error) {
      toast.error("Failed to submit price request");
    }
  };

  const handleDeleteService = async (serviceId) => {
    if (!window.confirm("Delete this payment?")) return;
    try {
      await api.delete(`/sales-services/${serviceId}`);
      toast.success("Payment deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete payment");
    }
  };

  // Toggle paid status for sales_services
  const handleTogglePaid = async (serviceId, isPaid) => {
    try {
      await api.put(`/sales-services/${serviceId}`, {
        is_paid: isPaid,
        paid_date: isPaid ? new Date().toISOString().split("T")[0] : null,
      });
      toast.success(isPaid ? "Marked as paid" : "Marked as unpaid");
      fetchData();
    } catch (error) {
      toast.error("Failed to update payment status");
    }
  };

  // Download Invoice PDF
  const handleDownloadInvoice = async (service) => {
    try {
      const response = await api.get(`/sales-services/${service.id}/invoice`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `invoice_${service.name || service.id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Failed to download invoice");
    }
  };

  // Download Act PDF
  const handleDownloadAct = async (service) => {
    try {
      const response = await api.get(`/sales-services/${service.id}/act`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `act_${service.name || service.id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Failed to download act");
    }
  };

  const handleDeleteTask = async (taskId, e) => {
    e.stopPropagation(); // Prevent row click navigation
    if (!window.confirm("Delete this task?")) return;
    try {
      await api.delete(`/tasks/${taskId}`);
      toast.success("Task deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete task");
    }
  };

  // Toggle payment status (paid/pending)
  const handleTogglePaymentStatus = async (paymentId, currentStatus) => {
    const newStatus = currentStatus === "paid" ? "pending" : "paid";
    try {
      const currentSchedule = project?.payment_schedule || [];
      const updatedSchedule = currentSchedule.map(ps => {
        const psId = ps.id || `ps-${currentSchedule.indexOf(ps)}`;
        if (psId === paymentId) {
          return { ...ps, status: newStatus };
        }
        return ps;
      });
      
      // Recalculate total_paid
      const totalPaid = updatedSchedule
        .filter(ps => ps.status === "paid")
        .reduce((sum, ps) => sum + (ps.amount || 0), 0);
      
      await api.put(`/trp/projects/${projectId}`, {
        payment_schedule: updatedSchedule,
        total_paid: totalPaid,
        total_pending: (project?.total_amount || 0) - totalPaid,
      });
      
      toast.success(newStatus === "paid" ? "Marked as paid" : "Marked as pending");
      fetchData();
    } catch (error) {
      toast.error("Failed to update payment status");
    }
  };

  // Toggle module payment status (paid/pending)
  const handleToggleModuleStatus = async (moduleIndex, currentStatus) => {
    const newStatus = currentStatus === "paid" ? "pending" : "paid";
    try {
      const currentModules = project?.modules || [];
      const updatedModules = currentModules.map((m, idx) => {
        if (idx === moduleIndex) {
          return { ...m, status: newStatus };
        }
        return m;
      });
      
      await api.put(`/trp/projects/${projectId}`, {
        modules: updatedModules,
      });
      
      toast.success(newStatus === "paid" ? "Marked as paid" : "Marked as pending");
      fetchData();
    } catch (error) {
      toast.error("Failed to update module status");
    }
  };

  // Open split payment dialog
  const handleOpenSplitDialog = (payment) => {
    setPaymentToSplit(payment);
    setSplitParts([
      { percent: 50, month: payment.month || monthOptions[0]?.value || "" },
      { percent: 50, month: monthOptions[1]?.value || "" },
    ]);
    setSplitPaymentDialogOpen(true);
  };

  // Add split part row
  const addSplitPart = () => {
    setSplitParts([...splitParts, { percent: 0, month: "" }]);
  };

  // Remove split part row
  const removeSplitPart = (idx) => {
    if (splitParts.length > 2) {
      setSplitParts(splitParts.filter((_, i) => i !== idx));
    }
  };

  // Update split part
  const updateSplitPart = (idx, field, value) => {
    const updated = [...splitParts];
    updated[idx] = { ...updated[idx], [field]: value };
    setSplitParts(updated);
  };

  // Execute split payment
  const handleSplitPayment = async () => {
    if (!paymentToSplit || splittingPayment) return;
    
    const totalPercent = splitParts.reduce((sum, p) => sum + (parseFloat(p.percent) || 0), 0);
    if (Math.abs(totalPercent - 100) > 0.5) {
      toast.error(`Total must equal 100% (currently ${totalPercent.toFixed(1)}%)`);
      return;
    }
    
    // Check all months are filled
    const emptyMonths = splitParts.filter(p => !p.month);
    if (emptyMonths.length > 0) {
      toast.error("Please select month for all parts");
      return;
    }
    
    setSplittingPayment(true);
    try {
      await api.post(`/sales-services/${paymentToSplit.id}/split-existing`, {
        splits: splitParts.map(p => ({
          percent: parseFloat(p.percent) || 0,
          month: p.month,
        })),
      });
      toast.success(`Split into ${splitParts.length} payments`);
      setSplitPaymentDialogOpen(false);
      setPaymentToSplit(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to split payment");
    } finally {
      setSplittingPayment(false);
    }
  };

  // Open edit date dialog
  const handleOpenEditDateDialog = (payment) => {
    setPaymentToEditDate(payment);
    setNewPlannedDate(payment.planned_date ? payment.planned_date.split('T')[0] : "");
    setEditDateDialogOpen(true);
  };

  // Save planned date
  const handleSavePlannedDate = async () => {
    if (!paymentToEditDate) return;
    
    try {
      await api.put(`/sales-services/${paymentToEditDate.id}`, {
        planned_date: newPlannedDate || null,
      });
      toast.success("Date updated");
      setEditDateDialogOpen(false);
      setPaymentToEditDate(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to update date");
    }
  };

  // Calculate totals from tasks (approved) and payments
  const totals = useMemo(() => {
    // Calculate from active tasks - convert to USD for consistent total
    const tasksBudgetUSD = tasks.reduce((sum, t) => {
      let price = t.client_price || 0;
      const currency = t.currency || "USD";
      // Convert to USD
      if (currency === "UAH") {
        price = price / (rates?.USD || 42);
      } else if (currency === "EUR") {
        price = price * 1.08; // EUR to USD approximate
      }
      return sum + price;
    }, 0);
    
    // Determine primary currency from tasks (most common currency)
    const taskCurrencies = tasks.map(t => t.currency || "USD");
    const currencyCount = taskCurrencies.reduce((acc, c) => {
      acc[c] = (acc[c] || 0) + 1;
      return acc;
    }, {});
    const primaryCurrency = Object.keys(currencyCount).length > 0 
      ? Object.entries(currencyCount).sort((a, b) => b[1] - a[1])[0][0]
      : "USD";
    
    // Calculate from payments
    let paymentsTotal = 0;
    let paymentsPaid = 0;
    services.forEach(s => {
      paymentsTotal += s.amount || 0;
      if (s.is_paid) paymentsPaid += s.amount || 0;
    });
    
    // Total Budget: 
    // - If tasks exist, use tasks total (converted to USD)
    // - Otherwise use project.total_amount (from modules/payments)
    const totalAmount = tasksBudgetUSD > 0 ? tasksBudgetUSD : (project?.total_amount || paymentsTotal);
    
    // Paid amount from payments
    const paidAmount = paymentsPaid || (project?.paid_amount || 0);
    
    return { 
      totalAmount, 
      paidAmount, 
      paidCount: services.filter(s => s.is_paid).length,
      tasksBudget: tasksBudgetUSD,
      paymentsTotal,
      paymentsPaid,
      currency: "USD"  // Total is always in USD now
    };
  }, [services, project, tasks, rates]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-orange-500"></div>
      </div>
    );
  }

  const isNew = projectId === "new";

  return (
    <div data-testid="partner-project-page" className="space-y-4 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/trp")} className="text-zinc-400 hover:text-zinc-50 h-8 w-8">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-xl font-bold text-zinc-50 font-['Manrope'] flex items-center gap-2">
              <Building2 className="h-5 w-5 text-emerald-500" />
              {isNew ? "New Project" : (project?.name || "Project")}
            </h1>
            <p className="text-xs text-zinc-500">Partner Project</p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button 
            onClick={handleSave} 
            disabled={saving}
            className="bg-orange-500 hover:bg-orange-600 text-white h-8 text-xs"
            data-testid="save-project-btn"
          >
            <Save className="h-3.5 w-3.5 mr-1" />
            {saving ? "Saving..." : "Save"}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4">
        {/* Project Info */}
        <div className="space-y-4">
          {/* Basic Info */}
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader className="py-3 px-4">
              <CardTitle className="text-zinc-50 font-['Manrope'] text-sm">Project Details</CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs text-zinc-400">Project Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="bg-zinc-950 border-zinc-800 text-zinc-50 h-8 text-sm"
                    placeholder="Project name"
                    data-testid="project-name-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-zinc-400">Client Name</Label>
                  <Input
                    value={formData.client_name}
                    onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                    className="bg-zinc-950 border-zinc-800 text-zinc-50 h-8 text-sm"
                    placeholder="Client name"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs text-zinc-400">Start Date <span className="text-red-400">*</span></Label>
                  <Input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    className="bg-zinc-950 border-zinc-800 text-zinc-50 h-8 text-sm"
                    required
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-zinc-400">Working Days</Label>
                  <Input
                    type="number"
                    value={formData.estimated_days}
                    onChange={(e) => setFormData({ ...formData, estimated_days: parseInt(e.target.value) || 0 })}
                    className="bg-zinc-950 border-zinc-800 text-zinc-50 h-8 text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-zinc-400">Deadline <span className="text-red-400">*</span></Label>
                  <Input
                    type="date"
                    value={formData.deadline}
                    onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
                    className="bg-zinc-950 border-zinc-800 text-zinc-50 h-8 text-sm"
                    required
                  />
                </div>
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs text-zinc-400">Communication Group Link</Label>
                <Input
                  value={formData.communication_group}
                  onChange={(e) => setFormData({ ...formData, communication_group: e.target.value })}
                  className="bg-zinc-950 border-zinc-800 text-zinc-50 h-8 text-sm"
                  placeholder="Telegram/WhatsApp group link"
                />
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs text-zinc-400">Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="bg-zinc-950 border-zinc-800 text-zinc-50 text-sm min-h-[60px]"
                  placeholder="Project notes..."
                />
              </div>
            </CardContent>
          </Card>

          {/* Tasks Section */}
          {!isNew && (
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="py-3 px-4">
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => setTasksExpanded(!tasksExpanded)}
                    className="flex items-center gap-2 text-zinc-50 font-['Manrope'] text-sm hover:text-orange-400 transition-colors"
                  >
                    {tasksExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    <ListTodo className="h-4 w-4 text-blue-500" />
                    Price Requests & Tasks ({priceRequests.length + tasks.length})
                  </button>
                  <div className="flex items-center gap-2">
                    {/* List / Gantt view toggle */}
                    <div className="inline-flex rounded border border-zinc-700 overflow-hidden">
                      <button
                        type="button"
                        onClick={() => setRequestsView("list")}
                        className={cn(
                          "px-2 py-1 text-[11px] flex items-center gap-1 transition-colors",
                          requestsView === "list" ? "bg-zinc-700 text-zinc-50" : "bg-zinc-900 text-zinc-400 hover:bg-zinc-800"
                        )}
                      >
                        <ListTodo className="h-3 w-3" /> List
                      </button>
                      <button
                        type="button"
                        onClick={() => setRequestsView("gantt")}
                        className={cn(
                          "px-2 py-1 text-[11px] flex items-center gap-1 border-l border-zinc-700 transition-colors",
                          requestsView === "gantt" ? "bg-zinc-700 text-zinc-50" : "bg-zinc-900 text-zinc-400 hover:bg-zinc-800"
                        )}
                      >
                        <Calendar className="h-3 w-3" /> Gantt
                      </button>
                    </div>
                    <Button
                      size="sm"
                      className="bg-orange-500 hover:bg-orange-600 text-white h-7 text-xs"
                      onClick={() => setShowBulkRequestModal(true)}
                      data-testid="bulk-price-request-btn"
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Bulk Request
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-zinc-700 text-zinc-50 h-7 text-xs hover:bg-zinc-800"
                      onClick={() => {
                        // Default to VEXOS task if project is vexos type
                        setNewTask(prev => ({ ...prev, is_vexos_task: isVexosProject }));
                        setShowNewTask(true);
                      }}
                      data-testid="add-price-request-btn"
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Single
                    </Button>
                  </div>
                </div>
              </CardHeader>
              {tasksExpanded && (
                <CardContent className="px-4 pb-4 space-y-4">
                  {/* Gantt view */}
                  {requestsView === "gantt" && (
                    <RequestsGantt
                      items={priceRequests.map((r) => ({
                        id: r.id,
                        label: r.task_name,
                        sublabel: `${(r.task_type || "").replace("_", " ")} × ${r.quantity || 1}`,
                        start_date: r.start_date,
                        end_date: r.end_date,
                        status: r.status,
                      }))}
                      emptyHint="No price requests yet."
                    />
                  )}
                  {/* Pending Price Requests */}
                  {requestsView === "list" && priceRequests.filter(r => r.status === "pending").length > 0 && (
                    <div className="space-y-2">
                      {/* Header with total sum and discount */}
                      {(() => {
                        const pendingReqs = priceRequests.filter(r => r.status === "pending");
                        const totalProposed = pendingReqs.reduce((sum, r) => sum + ((r.proposed_price || 0) * (r.quantity || 1)), 0);
                        const totalBase = pendingReqs.reduce((sum, r) => {
                          const basePrice = basePrices[r.task_type] || 0;
                          return sum + (basePrice * (r.quantity || 1));
                        }, 0);
                        const discountPercent = totalBase > 0 ? ((totalBase - totalProposed) / totalBase * 100) : 0;
                        
                        return (
                          <div className="p-2 rounded bg-orange-500/10 border border-orange-500/30 mb-2">
                            <div className="flex items-center justify-between">
                              <p className="text-xs text-orange-400 font-medium flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                Pending Approval ({pendingReqs.length})
                              </p>
                              <div className="text-right">
                                <p className="text-sm text-orange-400 font-mono font-bold">
                                  Total: ${totalProposed.toLocaleString()}
                                </p>
                                {totalBase > 0 && discountPercent !== 0 && (
                                  <p className={cn(
                                    "text-[10px] font-mono",
                                    discountPercent > 0 ? "text-red-400" : "text-emerald-400"
                                  )}>
                                    {discountPercent > 0 ? `↓ ${discountPercent.toFixed(1)}% discount` : `↑ ${Math.abs(discountPercent).toFixed(1)}% markup`}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                      <div className="space-y-1">
                        {priceRequests.filter(r => r.status === "pending").map((req) => {
                          const qty = req.quantity || 1;
                          const total = (req.proposed_price || 0) * qty;
                          const basePrice = basePrices[req.task_type] || 0;
                          const baseTotal = basePrice * qty;
                          const discountPercent = baseTotal > 0 ? ((baseTotal - total) / baseTotal * 100) : 0;
                          // Round to 1 decimal
                          const formatP = (v) => parseFloat(v || 0).toLocaleString(undefined, { maximumFractionDigits: 1 });
                          
                          return (
                            <div 
                              key={req.id}
                              className="flex items-center justify-between p-2 rounded bg-orange-500/10 border border-orange-500/20"
                            >
                              <div className="flex-1">
                                <p className="text-sm text-zinc-50">{req.task_name}</p>
                                <p className="text-xs text-zinc-400">
                                  {req.task_type?.replace("_", " ")} • Qty: {qty} × ${formatP(req.proposed_price)}
                                  {basePrice > 0 && (
                                    <span className="text-zinc-500 ml-1">(base: ${formatP(basePrice)})</span>
                                  )}
                                </p>
                                {req.discount_reason && (
                                  <p className="text-[10px] text-blue-400 mt-0.5" title={req.discount_reason}>
                                    💬 {req.discount_reason.length > 40 ? req.discount_reason.substring(0, 40) + "..." : req.discount_reason}
                                  </p>
                                )}
                              </div>
                              <div className="text-right">
                                <p className="text-sm text-orange-400 font-mono font-bold">${formatP(total)}</p>
                                {baseTotal > 0 && Math.abs(discountPercent) > 0.5 && (
                                  <p className={cn(
                                    "text-[10px] font-mono",
                                    discountPercent > 0 ? "text-red-400" : "text-emerald-400"
                                  )}>
                                    {discountPercent > 0 ? `-${discountPercent.toFixed(0)}%` : `+${Math.abs(discountPercent).toFixed(0)}%`}
                                  </p>
                                )}
                                <Badge variant="outline" className="text-[10px] bg-orange-500/20 text-orange-400 border-orange-500/30">
                                  Awaiting Approval
                                </Badge>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  {/* Approved Requests */}
                  {requestsView === "list" && priceRequests.filter(r => r.status === "approved").length > 0 && (
                    <div className="space-y-2">
                      <p className="text-xs text-emerald-400 font-medium flex items-center gap-1">
                        <CheckCircle2 className="h-3 w-3" />
                        Approved Requests ({priceRequests.filter(r => r.status === "approved").length})
                      </p>
                      <div className="space-y-1">
                        {priceRequests.filter(r => r.status === "approved").map((req) => {
                          const qty = req.quantity || 1;
                          const total = (req.proposed_price || 0) * qty;
                          const formatP = (v) => parseFloat(v || 0).toLocaleString(undefined, { maximumFractionDigits: 1 });
                          
                          return (
                            <div 
                              key={req.id}
                              className="flex items-center justify-between p-2 rounded bg-emerald-500/10 border border-emerald-500/20"
                            >
                              <div className="flex-1">
                                <p className="text-sm text-zinc-50">{req.task_name}</p>
                                <p className="text-xs text-zinc-400">
                                  {req.task_type?.replace("_", " ")} • Qty: {qty} • Approved by {req.approved_by}
                                </p>
                                {req.discount_reason && (
                                  <p className="text-[10px] text-blue-400 mt-0.5" title={req.discount_reason}>
                                    💬 {req.discount_reason.length > 40 ? req.discount_reason.substring(0, 40) + "..." : req.discount_reason}
                                  </p>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                <div className="flex items-center gap-1">
                                  <InlineDatePicker
                                    value={req.start_date}
                                    placeholder="Start"
                                    tone="blue"
                                    label="Planned start"
                                    readOnly={isPartner}
                                    onSave={(v) => handleUpdatePriceRequestDates(req.id, { start_date: v })}
                                  />
                                  <InlineDatePicker
                                    value={req.end_date}
                                    placeholder="End"
                                    tone="purple"
                                    label="Planned end"
                                    readOnly={isPartner}
                                    onSave={(v) => handleUpdatePriceRequestDates(req.id, { end_date: v })}
                                  />
                                </div>
                                <div className="text-right">
                                  <p className="text-sm text-emerald-400 font-mono font-bold">${formatP(total)}</p>
                                  <Badge variant="outline" className="text-[10px] bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
                                    Approved
                                  </Badge>
                                </div>
                                {/* Create Task button for admin/pm_lead */}
                                {(isAdmin || user?.role === "pm_lead" || user?.role === "team") && !req.task_created && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-7 px-2 text-xs border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
                                    onClick={async () => {
                                      try {
                                        await api.post(`/price-requests/${req.id}/create-task`);
                                        toast.success("Task created from request");
                                        fetchData();
                                      } catch (error) {
                                        toast.error("Failed to create task");
                                      }
                                    }}
                                  >
                                    <Plus className="h-3 w-3 mr-1" />
                                    Create Task
                                  </Button>
                                )}
                                {req.task_created && (
                                  <Badge variant="outline" className="text-[10px] bg-blue-500/20 text-blue-400 border-blue-500/30">
                                    Task Created
                                  </Badge>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  {/* Rejected Requests */}
                  {requestsView === "list" && priceRequests.filter(r => r.status === "rejected").length > 0 && (
                    <div className="space-y-2">
                      <p className="text-xs text-red-400 font-medium flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" />
                        Rejected ({priceRequests.filter(r => r.status === "rejected").length})
                      </p>
                      <div className="space-y-1">
                        {priceRequests.filter(r => r.status === "rejected").map((req) => (
                          <div 
                            key={req.id}
                            className="flex items-center justify-between p-2 rounded bg-red-500/10 border border-red-500/20"
                          >
                            <div className="flex-1">
                              <p className="text-sm text-zinc-50 line-through opacity-60">{req.task_name}</p>
                              <p className="text-xs text-red-400">
                                Reason: {req.rejection_reason || "Price not approved"}
                              </p>
                            </div>
                            <Badge variant="outline" className="text-[10px] bg-red-500/20 text-red-400 border-red-500/30">
                              Rejected
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Active Tasks (created by PM) */}
                  {requestsView === "list" && tasks.length === 0 && priceRequests.length === 0 ? (
                    <p className="text-sm text-zinc-500 text-center py-4">No price requests yet. Create your first request.</p>
                  ) : tasks.length > 0 && (
                    <>
                      <p className="text-xs text-blue-400 font-medium flex items-center gap-1 mt-4">
                        <ListTodo className="h-3 w-3" />
                        Tasks Created by PM ({tasks.length})
                      </p>
                      <Table>
                        <TableHeader>
                          <TableRow className="border-zinc-800 hover:bg-transparent">
                            <TableHead className="text-zinc-400 text-xs py-2">Task</TableHead>
                            <TableHead className="text-zinc-400 text-xs py-2">Type</TableHead>
                            {/* Artist Price column removed - internal only in unified-projects */}
                            <TableHead className="text-zinc-400 text-xs py-2 text-right">
                              {isPartner ? "Price" : "Client"}
                            </TableHead>
                            {!isPartner && (
                              <TableHead className="text-zinc-400 text-xs py-2">Status</TableHead>
                            )}
                            <TableHead className="text-zinc-400 text-xs py-2 text-center">Versions</TableHead>
                            <TableHead className="text-zinc-400 text-xs py-2">Progress</TableHead>
                            <TableHead className="text-zinc-400 text-xs py-2">Deadline</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {tasks.map((task) => {
                            const timeline = taskTimelines[task.id];
                            const taskCurrency = task.currency || "USD";
                            return (
                              <TableRow 
                                key={task.id} 
                                className="border-zinc-800 hover:bg-zinc-800/50 cursor-pointer"
                                onClick={() => navigate(`/tasks/${task.id}`)}
                              >
                                <TableCell className="py-2">
                                  <div className="flex flex-col">
                                    <span className="text-zinc-50 text-sm font-medium">{task.name}</span>
                                  </div>
                                </TableCell>
                                <TableCell className="py-2">
                                  <Badge variant="outline" className="bg-zinc-800 text-zinc-400 border-zinc-700 text-xs">
                                    {task.task_type?.replace("_", " ")}
                                  </Badge>
                                </TableCell>
                                {/* Artist Price cell removed - internal only */}
                                <TableCell className="py-2 text-right">
                                  <span className="text-emerald-400 font-mono text-sm">
                                    {formatAmount(task.client_price || 0, taskCurrency)}
                                  </span>
                                </TableCell>
                                {!isPartner && (
                                  <TableCell className="py-2">
                                    <TaskStageBadge task={task} size="sm" />
                                  </TableCell>
                                )}
                                <TableCell className="py-2 text-center">
                                  {(() => {
                                    const versions = task.versions || [];
                                    const totalVersions = versions.length;
                                    const revisions = versions.filter(v => 
                                      v.internal_status === 'rejected' || 
                                      v.client_approved === false && v.internal_status === 'approved'
                                    ).length;
                                    const currentVersion = totalVersions > 0 ? versions[totalVersions - 1] : null;
                                    const currentStatus = currentVersion?.internal_status;
                                    
                                    if (totalVersions === 0) {
                                      return <span className="text-xs text-zinc-600">—</span>;
                                    }
                                    
                                    return (
                                      <div className="flex flex-col items-center gap-0.5">
                                        <span className="text-xs text-zinc-50 font-medium">v{totalVersions}</span>
                                        {revisions > 0 && (
                                          <span className="text-[10px] text-orange-400">{revisions} rev</span>
                                        )}
                                        {currentStatus && (
                                          <Badge className={cn(
                                            "text-[9px] px-1 py-0",
                                            currentStatus === 'approved' ? "bg-green-900/50 text-green-400" :
                                            currentStatus === 'rejected' ? "bg-red-900/50 text-red-400" :
                                            "bg-yellow-900/50 text-yellow-400"
                                          )}>
                                            {currentStatus === 'pending' ? 'review' : currentStatus}
                                          </Badge>
                                        )}
                                      </div>
                                    );
                                  })()}
                                </TableCell>
                                <TableCell className="py-2 min-w-[140px]">
                                  {timeline ? (
                                    <TaskTimeline
                                      stages={timeline.stages}
                                      currentStageIndex={timeline.current_stage_index || 0}
                                      showLabels={false}
                                      size="sm"
                                    />
                                  ) : (
                                    <span className="text-xs text-zinc-600">—</span>
                                  )}
                                </TableCell>
                                <TableCell className="py-2 text-xs text-zinc-400">
                                  <div className="flex items-center justify-between gap-2">
                                    <span>{task.deadline ? new Date(task.deadline).toLocaleDateString() : "—"}</span>
                                    {(() => {
                                      const ty = (task.task_type || task.service_type || "").toLowerCase();
                                      const isSetupTask = ty && (setupChatKeywords || []).some((kw) => kw && ty.includes(kw));
                                      return isSetupTask ? (
                                        <span onClick={(e) => e.stopPropagation()}>
                                          <SetupChatButton
                                            taskId={task.id}
                                            serviceName={task.name}
                                            serviceType={task.task_type || task.service_type}
                                            canEdit={true}
                                            variant="icon"
                                          />
                                        </span>
                                      ) : null;
                                    })()}
                                  </div>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </>
                  )}
                </CardContent>
              )}
            </Card>
          )}

          {/* Payments Table - Sales style */}
          {!isNew && (
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="flex flex-row items-center justify-between py-3 px-4">
                <div className="flex items-center gap-3">
                  <CardTitle className="text-zinc-50 font-['Manrope'] text-sm flex items-center gap-2">
                    <DollarSign className="h-4 w-4 text-purple-400" />
                    Payments ({services.length})
                  </CardTitle>
                  {/* Summary inline */}
                  <div className="flex items-center gap-4 text-xs">
                    <span className="text-zinc-400">Total: <span className="text-zinc-50 font-medium">{formatAmount(totals.totalAmount, totals.currency)}</span></span>
                    <span className="text-zinc-400">Paid: <span className="text-emerald-400">{formatAmount(totals.paidAmount, totals.currency)}</span></span>
                    <span className="text-zinc-400">Pending: <span className="text-orange-400">{formatAmount(totals.totalAmount - totals.paidAmount, totals.currency)}</span></span>
                  </div>
                </div>
                {isAdmin && (
                  <Button 
                    size="sm" 
                    className="bg-purple-600 hover:bg-purple-700 text-white h-7 text-xs"
                    onClick={() => {
                      const defaultMonth = monthOptions[2]?.value || "";
                      setNewService({
                        service_type: "",
                        name: "",
                        totalAmount: "",
                        currency: "EUR",
                        month: defaultMonth,
                        payments: [{ month: defaultMonth, percent: 100, amount: "" }],
                        splitPayments: false,
                      });
                      setShowNewService(true);
                    }}
                    data-testid="add-payment-btn"
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Add Payment
                  </Button>
                )}
              </CardHeader>
              <CardContent className="px-4 pb-4">
                {services.length === 0 ? (
                  <p className="text-sm text-zinc-500 text-center py-4">No payments scheduled yet</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow className="border-zinc-800 hover:bg-transparent">
                        <TableHead className="text-zinc-400 text-xs py-2">Payment</TableHead>
                        <TableHead className="text-zinc-400 text-xs py-2">Month</TableHead>
                        <TableHead className="text-zinc-400 text-xs py-2">Currency</TableHead>
                        <TableHead className="text-zinc-400 text-xs py-2 text-right">Amount</TableHead>
                        <TableHead className="text-zinc-400 text-xs py-2 text-center">Paid</TableHead>
                        <TableHead className="text-zinc-400 text-xs py-2">Paid Date</TableHead>
                        <TableHead className="text-zinc-400 text-xs py-2 text-center">Docs</TableHead>
                        <TableHead className="text-zinc-400 w-[80px]"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {services.map(service => (
                        <TableRow key={service.id} className={cn("border-zinc-800", service.is_paid && "bg-green-950/20")}>
                          <TableCell className="py-1.5">
                            <div className="flex items-center gap-1.5">
                              <span className="text-zinc-50 text-sm">{service.name}</span>
                              {service.payment_group_id && (
                                <Badge className="text-[10px] h-4 bg-purple-900/30 text-purple-400 border-purple-700">Split</Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-zinc-400 font-mono text-xs py-1.5">{service.month || "—"}</TableCell>
                          <TableCell className="py-1.5">
                            <Badge variant="outline" className="text-[10px] bg-blue-500/10 text-blue-400 border-blue-500/30">
                              {service.currency || "EUR"}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right py-1.5">
                            <span className="font-mono text-green-400 text-sm">{formatAmount(service.amount || 0, service.currency || "EUR")}</span>
                          </TableCell>
                          <TableCell className="text-center py-1.5">
                            <div className="flex items-center justify-center gap-1">
                              {isAdmin ? (
                                <Switch
                                  checked={service.is_paid}
                                  onCheckedChange={(checked) => handleTogglePaid(service.id, checked)}
                                  className="data-[state=checked]:bg-green-600 scale-90"
                                />
                              ) : (
                                <Badge className={service.is_paid ? "bg-green-900/30 text-green-400" : "bg-yellow-900/30 text-yellow-400"}>
                                  {service.is_paid ? "Paid" : "Pending"}
                                </Badge>
                              )}
                              {isAdmin && (service.is_paid ? (
                                <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
                              ) : (
                                <Clock className="h-3.5 w-3.5 text-zinc-500" />
                              ))}
                            </div>
                          </TableCell>
                          <TableCell className="text-zinc-400 text-xs py-1.5">{service.paid_date || "—"}</TableCell>
                          <TableCell className="py-1.5">
                            <div className="flex items-center gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDownloadInvoice(service)}
                                className="h-6 px-2 text-xs text-blue-400 hover:text-blue-300 hover:bg-blue-500/10"
                                title="Download Invoice"
                              >
                                <FileSignature className="h-3 w-3 mr-1" />
                                Inv
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDownloadAct(service)}
                                className="h-6 px-2 text-xs text-green-400 hover:text-green-300 hover:bg-green-500/10"
                                title="Download Act"
                              >
                                <Download className="h-3 w-3 mr-1" />
                                Act
                              </Button>
                            </div>
                          </TableCell>
                          <TableCell className="py-1.5">
                            <div className="flex items-center gap-1">
                              {/* Split button - only for unpaid payments without split group */}
                              {!service.is_paid && !service.payment_group_id && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleOpenSplitDialog(service)}
                                  className="h-6 w-6 p-0 text-purple-400 hover:text-purple-300 hover:bg-purple-500/10"
                                  title="Split into multiple payments"
                                >
                                  <Split className="h-3 w-3" />
                                </Button>
                              )}
                              {/* Edit date button */}
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-6 w-6 p-0 text-sky-400 hover:text-sky-300 hover:bg-sky-900/20"
                                onClick={() => handleOpenEditDateDialog(service)}
                                title="Set planned payment date"
                              >
                                <Calendar className="h-3 w-3" />
                              </Button>
                              {isAdmin && (
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  onClick={() => handleDeleteService(service.id)} 
                                  className="h-6 w-6 p-0 text-red-400 hover:text-red-300"
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Add Payment Dialog */}
      <Dialog open={showNewService} onOpenChange={setShowNewService}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-zinc-50">Add Payment</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="space-y-1">
              <Label className="text-xs text-zinc-400">Description</Label>
              <Input
                value={newService.name}
                onChange={(e) => setNewService({ ...newService, name: e.target.value })}
                className="bg-zinc-950 border-zinc-800 text-zinc-50 h-8 text-sm"
                placeholder="Payment description (optional)"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-xs text-zinc-400">Amount *</Label>
                <Input
                  type="number"
                  value={newService.totalAmount}
                  onChange={(e) => setNewService({ ...newService, totalAmount: e.target.value })}
                  className="bg-zinc-950 border-zinc-800 text-zinc-50 h-8 text-sm"
                  placeholder="0"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-zinc-400">Planned Date</Label>
                <Select 
                  value={newService.payments[0]?.month} 
                  onValueChange={(v) => setNewService({ 
                    ...newService, 
                    payments: [{ ...newService.payments[0], month: v }] 
                  })}
                >
                  <SelectTrigger className="bg-zinc-950 border-zinc-800 text-zinc-50 h-8 text-sm">
                    <SelectValue placeholder="Select month" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    {monthOptions.map((m) => (
                      <SelectItem key={m.value} value={m.value} className="text-zinc-300">{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowNewService(false)} className="border-zinc-700">
              Cancel
            </Button>
            <Button onClick={handleAddService} className="bg-emerald-600 hover:bg-emerald-700">
              Add Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Task Dialog */}
      <Dialog open={showNewTask} onOpenChange={setShowNewTask}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-zinc-50">
              {newTask.is_vexos_task ? "Create VEXOS Task" : "Create Task"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="space-y-1">
              <Label className="text-xs text-zinc-400">Task Name *</Label>
              <Input
                value={newTask.name}
                onChange={(e) => setNewTask({ ...newTask, name: e.target.value })}
                className="bg-zinc-950 border-zinc-800 text-zinc-50 h-8 text-sm"
                placeholder="Task name"
                data-testid="task-name-input"
              />
            </div>
            
            {/* Task Type Toggle - VEXOS or Regular */}
            <div className="flex items-center gap-3 p-2 bg-zinc-800/50 rounded-lg">
              <Label className="text-xs text-zinc-400">Task Type:</Label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setNewTask({ ...newTask, is_vexos_task: true })}
                  className={`px-3 py-1 text-xs rounded-md transition-colors ${
                    newTask.is_vexos_task 
                      ? 'bg-orange-500 text-white' 
                      : 'bg-zinc-700 text-zinc-400 hover:bg-zinc-600'
                  }`}
                >
                  VEXOS
                </button>
                <button
                  type="button"
                  onClick={() => setNewTask({ ...newTask, is_vexos_task: false })}
                  className={`px-3 py-1 text-xs rounded-md transition-colors ${
                    !newTask.is_vexos_task 
                      ? 'bg-blue-500 text-white' 
                      : 'bg-zinc-700 text-zinc-400 hover:bg-zinc-600'
                  }`}
                >
                  Regular
                </button>
              </div>
            </div>
            
            {/* VEXOS Task Form - shown when is_vexos_task is true */}
            {newTask.is_vexos_task ? (
              <div className="space-y-4 p-3 bg-zinc-800/30 rounded-lg border border-zinc-700">
                <div className="flex items-center gap-2">
                  <DollarSign className="h-4 w-4 text-orange-400" />
                  <span className="text-sm font-medium text-zinc-50">VEXOS Components</span>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  {/* Base Fee - checkbox toggle */}
                  <div className="flex items-center justify-between p-2 bg-zinc-800/50 rounded border border-zinc-700">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={newTask.vexos.base_fee_qty === 1}
                        onChange={(e) => setNewTask({
                          ...newTask,
                          vexos: { ...newTask.vexos, base_fee_qty: e.target.checked ? 1 : 0 }
                        })}
                        className="rounded bg-zinc-800 border-zinc-700"
                      />
                      <span className="text-xs text-zinc-50">Base Fee (${vexosFees.base_fee})</span>
                    </label>
                    {newTask.vexos.base_fee_qty === 1 && (
                      <span className="text-sm text-orange-400 font-mono">${vexosFees.base_fee.toLocaleString()}</span>
                    )}
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-zinc-400">Orbit Fee (${vexosFees.orbit_fee})</Label>
                    <Input
                      type="number"
                      min="0"
                      value={newTask.vexos.orbit_fee_qty}
                      onChange={(e) => setNewTask({ 
                        ...newTask, 
                        vexos: { ...newTask.vexos, orbit_fee_qty: parseInt(e.target.value) || 0 }
                      })}
                      className="bg-zinc-950 border-zinc-800 text-zinc-50 h-8 text-sm"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-zinc-400">Apartments (${vexosFees.unit_fee_apartments}/unit)</Label>
                    <Input
                      type="number"
                      min="0"
                      value={newTask.vexos.unit_apartments_qty}
                      onChange={(e) => setNewTask({ 
                        ...newTask, 
                        vexos: { ...newTask.vexos, unit_apartments_qty: parseInt(e.target.value) || 0 }
                      })}
                      className="bg-zinc-950 border-zinc-800 text-zinc-50 h-8 text-sm"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-zinc-400">Villas (${vexosFees.unit_fee_villas}/unit)</Label>
                    <Input
                      type="number"
                      min="0"
                      value={newTask.vexos.unit_villas_qty}
                      onChange={(e) => setNewTask({ 
                        ...newTask, 
                        vexos: { ...newTask.vexos, unit_villas_qty: parseInt(e.target.value) || 0 }
                      })}
                      className="bg-zinc-950 border-zinc-800 text-zinc-50 h-8 text-sm"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-zinc-400">360° Views (${vexosFees.fee_360})</Label>
                    <Input
                      type="number"
                      min="0"
                      value={newTask.vexos.fee_360_qty}
                      onChange={(e) => setNewTask({ 
                        ...newTask, 
                        vexos: { ...newTask.vexos, fee_360_qty: parseInt(e.target.value) || 0 }
                      })}
                      className="bg-zinc-950 border-zinc-800 text-zinc-50 h-8 text-sm"
                    />
                  </div>
                </div>
                
                <div className="flex flex-wrap gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newTask.vexos.custom_design}
                      onChange={(e) => setNewTask({
                        ...newTask,
                        vexos: { ...newTask.vexos, custom_design: e.target.checked }
                      })}
                      className="rounded bg-zinc-800 border-zinc-700"
                    />
                    <span className="text-xs text-zinc-300">Custom Design (+${vexosFees.custom_design})</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newTask.vexos.rush_deadline}
                      onChange={(e) => setNewTask({
                        ...newTask,
                        vexos: { ...newTask.vexos, rush_deadline: e.target.checked }
                      })}
                      className="rounded bg-zinc-800 border-zinc-700"
                    />
                    <span className="text-xs text-zinc-300">Rush Deadline (+${vexosFees.rush_deadline})</span>
                  </label>
                </div>
                
                {/* Orbit Animation Rendering - based on orbit count */}
                <div className="flex items-center justify-between p-2 bg-zinc-800/50 rounded border border-zinc-700">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newTask.vexos.orbit_animation}
                      onChange={(e) => setNewTask({
                        ...newTask,
                        vexos: { ...newTask.vexos, orbit_animation: e.target.checked }
                      })}
                      className="rounded bg-zinc-800 border-zinc-700"
                    />
                    <div>
                      <span className="text-xs text-zinc-50">Orbit Animation Rendering</span>
                      <span className="text-xs text-orange-400 ml-2">$2500 first + $1000/extra</span>
                    </div>
                  </label>
                  {newTask.vexos.orbit_animation && newTask.vexos.orbit_fee_qty > 0 && (
                    <div className="text-right">
                      <span className="text-xs text-zinc-500">{newTask.vexos.orbit_fee_qty} orbit{newTask.vexos.orbit_fee_qty > 1 ? 's' : ''}</span>
                      <span className="text-sm text-orange-400 font-mono ml-2">
                        ${calculateVexosTotal().orbitAnimationCost?.toLocaleString()}
                      </span>
                    </div>
                  )}
                </div>
                
                <div className="space-y-1">
                  <Label className="text-xs text-zinc-400">Client Invoice Total (for {vexosFees.vexos_commission_percent}% commission)</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400">$</span>
                    <Input
                      type="number"
                      min="0"
                      value={newTask.vexos.client_invoice}
                      onChange={(e) => setNewTask({ 
                        ...newTask, 
                        vexos: { ...newTask.vexos, client_invoice: parseFloat(e.target.value) || 0 }
                      })}
                      className="bg-zinc-950 border-zinc-800 text-zinc-50 h-8 text-sm pl-7"
                      placeholder="Total invoice to client"
                    />
                  </div>
                </div>
                
                {/* VEXOS Total Summary */}
                <div className="p-2 bg-zinc-900/50 rounded border border-zinc-700">
                  <div className="flex justify-between text-xs">
                    <span className="text-zinc-400">Components:</span>
                    <span className="text-zinc-50 font-mono">${calculateVexosTotal().componentsTotal.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-xs mt-1">
                    <span className="text-zinc-400">Commission ({vexosFees.vexos_commission_percent}%):</span>
                    <span className="text-zinc-50 font-mono">${calculateVexosTotal().commission.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm mt-2 pt-2 border-t border-zinc-700">
                    <span className="text-zinc-300 font-medium">Total VEXOS Revenue:</span>
                    <span className="text-emerald-400 font-mono font-bold">${calculateVexosTotal().total.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            ) : (
              /* Regular Task Form */
              <>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label className="text-xs text-zinc-400">Task Type *</Label>
                    <Select 
                      value={newTask.task_type} 
                      onValueChange={(v) => {
                        const typeInfo = serviceTypesInfo.find(t => t.name === v);
                        const suggestedPrice = typeInfo?.partner_price || suggestedPrices[v] || 0;
                        setNewTask({ ...newTask, task_type: v, client_price: suggestedPrice, sq_ft: 0 });
                      }}
                    >
                      <SelectTrigger className="bg-zinc-950 border-zinc-800 text-zinc-50 h-8 text-sm" data-testid="partner-task-type-select">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800 max-h-60">
                        {serviceTypes.map((type) => {
                          const typeInfo = serviceTypesInfo.find(t => t.name === type);
                          const price = typeInfo?.partner_price || suggestedPrices[type] || 0;
                          return (
                            <SelectItem key={type} value={type} className="text-zinc-300">
                              {type}
                              {typeInfo?.is_sq_ft_pricing && <span className="ml-1 text-blue-400">(sq ft)</span>}
                              {price > 0 && (
                                <span className="ml-2 text-emerald-400">(${price})</span>
                              )}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-zinc-400">Deadline</Label>
                    <Input
                      type="date"
                      value={newTask.deadline}
                      onChange={(e) => setNewTask({ ...newTask, deadline: e.target.value })}
                      className="bg-zinc-950 border-zinc-800 text-zinc-50 h-8 text-sm"
                    />
                  </div>
                </div>
                
                {/* Square Footage Input (for sq ft pricing types) */}
                {isSqFtPricing() && (
                  <div className="p-3 bg-blue-500/10 rounded border border-blue-500/30 space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-blue-400 font-medium">Square Footage Pricing</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-1">
                        <Label className="text-xs text-zinc-400">Square Feet</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.1"
                          value={newTask.sq_ft}
                          onChange={(e) => setNewTask({ ...newTask, sq_ft: parseFloat(e.target.value) || 0 })}
                          className="bg-zinc-950 border-zinc-800 text-zinc-50 h-8 text-sm"
                          placeholder="Enter sq ft"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-zinc-400">Price per sq ft</Label>
                        <div className="h-8 flex items-center text-zinc-50 font-mono text-sm">
                          ${basePrices[newTask.task_type] || 0}
                        </div>
                      </div>
                    </div>
                    <div className="flex justify-between items-center pt-2 border-t border-blue-500/30">
                      <span className="text-xs text-zinc-400">Calculated Price:</span>
                      <span className="text-emerald-400 font-mono font-bold">${calculateSqFtPrice().toFixed(2)}</span>
                    </div>
                  </div>
                )}
                
                {/* Regular Price Input (for non-sq ft types) */}
                {!isSqFtPricing() && (
                  <div className="space-y-1">
                    <Label className="text-xs text-zinc-400">
                      Estimated Price
                      {newTask.task_type && suggestedPrices[newTask.task_type] > 0 && (
                        <span className="text-emerald-400 ml-2">
                          (suggested: ${suggestedPrices[newTask.task_type]})
                        </span>
                      )}
                    </Label>
                    <Input
                      type="number"
                      value={newTask.client_price}
                      onChange={(e) => setNewTask({ ...newTask, client_price: parseFloat(e.target.value) || 0 })}
                      className="bg-zinc-950 border-zinc-800 text-zinc-50 h-8 text-sm"
                      placeholder="0"
                    />
                    
                    {/* Margin Calculator */}
                    {newTask.task_type && basePrices[newTask.task_type] > 0 && (
                      <div className={cn(
                        "mt-2 p-2 rounded text-xs",
                        (newTask.client_price || 0) < basePrices[newTask.task_type]
                          ? "bg-red-500/10 border border-red-500/30"
                          : "bg-emerald-500/10 border border-emerald-500/30"
                      )}>
                        <div className="flex justify-between items-center">
                          <span className="text-zinc-400">Base Price:</span>
                          <span className="text-zinc-50 font-mono">${basePrices[newTask.task_type]}</span>
                        </div>
                        <div className="flex justify-between items-center mt-1">
                          <span className="text-zinc-400">Your Price:</span>
                          <span className="text-zinc-50 font-mono">${newTask.client_price || 0}</span>
                        </div>
                        <div className="flex justify-between items-center mt-1 border-t border-zinc-700 pt-1">
                          <span className="text-zinc-400">Margin:</span>
                          <span className={cn(
                            "font-mono font-medium",
                            (newTask.client_price || 0) >= basePrices[newTask.task_type] ? "text-emerald-400" : "text-red-400"
                          )}>
                            {basePrices[newTask.task_type] > 0 
                              ? (((newTask.client_price || 0) - basePrices[newTask.task_type]) / basePrices[newTask.task_type] * 100).toFixed(1) 
                              : 0}%
                          </span>
                        </div>
                        {(newTask.client_price || 0) < basePrices[newTask.task_type] && (
                          <p className="text-red-400 mt-1 text-[10px]">
                            ⚠️ Price is below base - will require approval
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
                
                {/* Quantity */}
                <div className="space-y-1">
                  <Label className="text-xs text-zinc-400">
                    Quantity
                    <span className="text-zinc-500 ml-1">(max 20)</span>
                  </Label>
                  <Input
                    type="number"
                    min="1"
                    max="20"
                    value={newTask.quantity}
                    onChange={(e) => setNewTask({ ...newTask, quantity: Math.min(20, Math.max(1, parseInt(e.target.value) || 1)) })}
                    className="bg-zinc-950 border-zinc-800 text-zinc-50 h-8 text-sm w-20"
                  />
                  {newTask.quantity > 1 && (
                    <p className="text-xs text-orange-400 mt-1">
                      Creates {newTask.quantity} tasks: "{newTask.name || 'Task'} (1 of {newTask.quantity})", etc.
                    </p>
                  )}
                </div>

                {/* Additional 360-tour points (auto-calc at $20/pt) */}
                <div className="space-y-1 rounded border border-purple-500/20 bg-purple-500/5 p-2">
                  <Label className="text-xs text-purple-300">
                    Additional 360° tour points ($20/point)
                  </Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min="0"
                      value={newTask.extra_360_points || 0}
                      onChange={(e) => setNewTask({ ...newTask, extra_360_points: Math.max(0, parseInt(e.target.value) || 0) })}
                      className="bg-zinc-950 border-zinc-800 text-zinc-50 h-8 text-sm w-24"
                      placeholder="0"
                      data-testid="partner-extra-360-points"
                    />
                    <span className="text-[11px] text-zinc-400">
                      × $20 = <span className="font-mono text-purple-300">${(parseInt(newTask.extra_360_points) || 0) * 20}</span>
                    </span>
                  </div>
                  <p className="text-[10px] text-zinc-500">
                    Added on top of the request total.
                  </p>
                </div>
              </>
            )}
            
            <div className="space-y-1">
              <Label className="text-xs text-zinc-400">Description</Label>
              <Textarea
                value={newTask.description}
                onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                className="bg-zinc-950 border-zinc-800 text-zinc-50 text-sm min-h-[60px]"
                placeholder="Task description..."
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-zinc-400">Brief / Reference Details</Label>
              <Textarea
                value={newTask.brief}
                onChange={(e) => setNewTask({ ...newTask, brief: e.target.value })}
                className="bg-zinc-950 border-zinc-800 text-zinc-50 text-sm min-h-[60px]"
                placeholder="Detailed brief, references, requirements..."
              />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowNewTask(false)} className="border-zinc-700">
              Cancel
            </Button>
            <Button 
              onClick={handleAddTask} 
              className="bg-blue-600 hover:bg-blue-700" 
              data-testid="create-task-btn"
            >
              {newTask.is_vexos_task ? "Submit VEXOS for Approval" : (basePrices[newTask.task_type] > 0 ? "Submit for Approval" : "Create Task")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Bulk Price Request Modal */}
      <BulkPriceRequestModal
        open={showBulkRequestModal}
        onOpenChange={setShowBulkRequestModal}
        serviceTypes={serviceTypesInfo}
        basePrices={basePrices}
        projectId={projectId}
        vexosFees={vexosFees}
        onSubmit={async (requests) => {
          // Submit all price requests
          for (const req of requests) {
            await api.post("/price-requests", req);
          }
          fetchData();
        }}
      />

      {/* Split Payment Dialog */}
      <Dialog open={splitPaymentDialogOpen} onOpenChange={(open) => {
        if (!open) {
          setSplitPaymentDialogOpen(false);
          setPaymentToSplit(null);
        }
      }}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-zinc-50">Split Payment</DialogTitle>
          </DialogHeader>
          
          {paymentToSplit && (
            <div className="space-y-4">
              {/* Original Payment Info */}
              <div className="p-3 bg-zinc-800/50 rounded-lg border border-zinc-700">
                <p className="text-sm text-zinc-400">Splitting payment:</p>
                <p className="text-lg font-semibold text-zinc-50">{paymentToSplit.name}</p>
                <p className="text-sm text-green-400 font-mono">
                  {formatAmount(paymentToSplit.amount, paymentToSplit.currency || "EUR")}
                </p>
              </div>

              {/* Split Parts */}
              <div className="space-y-3">
                <Label className="text-zinc-400">Split into parts:</Label>
                {splitParts.map((part, idx) => (
                  <div key={idx} className="flex items-center gap-2 p-2 bg-zinc-800/30 rounded border border-zinc-700">
                    <span className="text-zinc-500 text-sm w-6">{idx + 1}.</span>
                    <div className="flex items-center gap-1">
                      <Input
                        type="number"
                        value={part.percent}
                        onChange={(e) => updateSplitPart(idx, "percent", e.target.value)}
                        className="bg-zinc-800 border-zinc-700 text-zinc-50 w-20 h-8 text-sm text-center"
                        placeholder="%"
                      />
                      <span className="text-zinc-500 text-sm">%</span>
                    </div>
                    <span className="text-zinc-600">=</span>
                    <span className="text-green-400 font-mono text-sm w-24">
                      {formatAmount((paymentToSplit.amount * (parseFloat(part.percent) || 0)) / 100, paymentToSplit.currency || "EUR")}
                    </span>
                    <Select value={part.month} onValueChange={(v) => updateSplitPart(idx, "month", v)}>
                      <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-50 w-32 h-8 text-sm">
                        <SelectValue placeholder="Month" />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-800 border-zinc-700">
                        {monthOptions.map(m => (
                          <SelectItem key={m.value} value={m.value} className="text-zinc-50 text-sm">{m.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {splitParts.length > 2 && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => removeSplitPart(idx)}
                        className="h-7 w-7 p-0 text-red-400 hover:text-red-300"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>

              {/* Add Row & Total */}
              <div className="flex items-center justify-between">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={addSplitPart}
                  className="text-purple-400 border-purple-700 hover:bg-purple-900/30"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Add Part
                </Button>
                <span className={cn(
                  "text-sm font-mono",
                  Math.abs(splitParts.reduce((s, p) => s + (parseFloat(p.percent) || 0), 0) - 100) < 0.5
                    ? "text-green-400"
                    : "text-yellow-400"
                )}>
                  Total: {splitParts.reduce((s, p) => s + (parseFloat(p.percent) || 0), 0).toFixed(1)}%
                </span>
              </div>

              <p className="text-xs text-zinc-500">
                Original payment will be deleted and replaced with {splitParts.length} new payments.
              </p>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setSplitPaymentDialogOpen(false)}
              className="border-zinc-700 text-zinc-400"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSplitPayment}
              disabled={splittingPayment}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              {splittingPayment ? "Splitting..." : `Split into ${splitParts.length} Payments`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Payment Date Dialog */}
      <Dialog open={editDateDialogOpen} onOpenChange={(open) => {
        if (!open) {
          setEditDateDialogOpen(false);
          setPaymentToEditDate(null);
        }
      }}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-zinc-50">Set Planned Payment Date</DialogTitle>
          </DialogHeader>
          
          {paymentToEditDate && (
            <div className="space-y-4">
              <div className="p-3 bg-zinc-800/50 rounded-lg border border-zinc-700">
                <p className="text-sm text-zinc-50 font-medium">{paymentToEditDate.name}</p>
                <p className="text-sm text-green-400 font-mono">
                  {formatAmount(paymentToEditDate.amount, paymentToEditDate.currency || "EUR")}
                </p>
              </div>
              
              <div className="space-y-2">
                <Label className="text-zinc-400">Planned payment date</Label>
                <Input
                  type="date"
                  value={newPlannedDate}
                  onChange={(e) => setNewPlannedDate(e.target.value)}
                  className="bg-zinc-800 border-zinc-700 text-zinc-50"
                />
                <p className="text-xs text-zinc-500">
                  This helps the manager know when you plan to make this payment
                </p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditDateDialogOpen(false)}
              className="border-zinc-700 text-zinc-400"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSavePlannedDate}
              className="bg-sky-600 hover:bg-sky-700 text-white"
            >
              Save Date
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PartnerProjectPage;
